import { SITE_CONFIG } from "../../app/config";
import { useHead, buildBreadcrumbSchema } from "../../seo";
import { guides, GUIDE_CATEGORIES, getGuidesByCategory } from "./guide-data";
import type { GuideCategory } from "./guide-data";

const categoryIcons: Record<GuideCategory, string> = {
  "Getting Started": "🚀",
  "Equipment Setup": "🔧",
  "Vinyl Care": "🧹",
  "Collecting Skills": "📦",
  "Digital & Hybrid": "💻",
  "Advanced": "🎓",
};

export const GuideIndex = (): JSX.Element => {
  useHead({
    title: "Vinyl & CD Guides — Step-by-Step How-To Articles",
    description:
      "Step-by-step guides for vinyl and CD collecting. Learn how to set up a turntable, clean records, rip CDs, build a collection, and more.",
    path: "/guides",
    ogType: "website",
    jsonLd: buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Guides", url: "/guides" },
    ]),
  });

  return (
    <div className="shell">
      <div className="pseo-page">
        <header className="pseo-header">
          <a href="/" className="blog-back">
            ← Back to {SITE_CONFIG.name}
          </a>
          <h1 className="pseo-title">How-To Guides</h1>
          <p className="pseo-subtitle">
            Step-by-step instructions for every aspect of vinyl and CD collecting.{" "}
            <strong>{guides.length} guides</strong> from beginner basics to advanced techniques.
          </p>
        </header>

        <div className="guide-categories">
          {GUIDE_CATEGORIES.map((category) => {
            const items = getGuidesByCategory(category);
            if (items.length === 0) return null;
            return (
              <section key={category} className="guide-category-section">
                <h2 className="guide-category-heading">
                  <span className="guide-category-icon">{categoryIcons[category]}</span>
                  {category}
                  <span className="guide-category-count">{items.length}</span>
                </h2>
                <div className="guide-card-grid">
                  {items.map((guide) => (
                    <a
                      key={guide.slug}
                      href={`/guides/${guide.slug}`}
                      className="guide-card"
                    >
                      <h3 className="guide-card-title">{guide.title}</h3>
                      <p className="guide-card-desc">{guide.metaDescription}</p>
                      <div className="guide-card-meta">
                        <span className="guide-card-steps">{guide.steps.length} steps</span>
                        <span className="guide-card-link">Read guide →</span>
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <footer className="pseo-footer">
          <div className="blog-cta">
            <h3>Ready to Put These Guides into Practice?</h3>
            <p>
              Paste your Spotify playlist and get a shopping list of vinyl & CDs to start your
              collection. Free, instant, no signup required.
            </p>
            <a href="/" className="blog-cta-button">
              Try {SITE_CONFIG.name} →
            </a>
          </div>
          <div className="pseo-nav-links">
            <a href="/best">Best Of</a>
            <a href="/glossary">Music Glossary</a>
            <a href="/compare">Compare Formats</a>
            <a href="/blog">Blog</a>
            <a href="/">Home</a>
          </div>
        </footer>
      </div>
    </div>
  );
};
